// ================================
// CUSTOMER REWARDS PAGE
// Points balance (never blended with coins), tier ladder, referral link,
// and the points-redemption catalog.
// ================================
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../../context/SocketContext';
import axios from 'axios';

const TIER_COLORS = { Mercury:'#8a94a6', Mars:'#e86a3a', Jupiter:'#f0a020', 'Weedo World':'#c0101a' };
const TIER_ICONS  = { Mercury:'☿', Mars:'♂', Jupiter:'♃', 'Weedo World':'🌍' };

export default function CustomerRewards() {
  const navigate = useNavigate();
  const socket   = useSocket();
  const [points, setPoints]         = useState(0);
  const [tier, setTier]             = useState(null);
  const [next, setNext]             = useState(null);
  const [tiers, setTiers]           = useState([]);
  const [referralLink, setReferralLink] = useState('');
  const [referral, setReferral] = useState(null); // { enabled, effectiveReferrerPoints, effectiveReferrerCoins }
  const [redeemable, setRedeemable] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [redeeming, setRedeeming]   = useState(null); // `${strain}-${size}` while in flight
  const [copied, setCopied]         = useState(false);
  const [msg, setMsg]               = useState('');

  useEffect(() => { fetchMe(); fetchRedeemable(); fetchReferralSettings(); }, []);

  useEffect(() => {
    if (!socket) return;
    socket.on('points:updated', ({ balance }) => setPoints(balance));
    return () => socket.off('points:updated');
  }, [socket]);

  async function fetchMe() {
    try {
      const res = await axios.get('/api/loyalty/me');
      setPoints(res.data.points);
      setTier(res.data.tier);
      setNext(res.data.next);
      setTiers(res.data.tiers);
      setReferralLink(res.data.referralLink);
    } catch (e) {}
    finally { setLoading(false); }
  }

  async function fetchRedeemable() {
    try {
      const res = await axios.get('/api/loyalty/redeemable');
      setRedeemable(res.data.items || []);
    } catch (e) {}
  }

  async function fetchReferralSettings() {
    try {
      const res = await axios.get('/api/settings/referral');
      setReferral(res.data);
    } catch (e) {}
  }

  async function handleRedeem(strain, size, cost) {
    if (points < cost) return;
    if (!window.confirm(`Redeem ${cost} points for ${strain} (${size})?`)) return;
    setRedeeming(`${strain}-${size}`);
    setMsg('');
    try {
      const res = await axios.post('/api/loyalty/redeem', { strain, size });
      setPoints(res.data.balance);
      setTier(res.data.tier);
      setMsg(`✅ Redeemed ${strain} (${size}) — let your driver or admin know next time you order.`);
    } catch (e) {
      setMsg(`❌ ${e.response?.data?.error || 'Redemption failed'}`);
    } finally {
      setRedeeming(null);
    }
  }

  async function handleShare() {
    if (navigator.share) {
      try { await navigator.share({ title: 'Join Weedo World', text: 'Join me on Weedo World and we both get rewarded.', url: referralLink }); return; }
      catch (e) { /* user cancelled — fall through to copy */ }
    }
    handleCopy();
  }

  function handleCopy() {
    navigator.clipboard?.writeText(referralLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  // Ladder renders low -> high; backend's `tiers` comes high -> low.
  const ladder = [...tiers].reverse();
  const highestThreshold = ladder.length ? ladder[ladder.length - 1].threshold : 1000;
  const progressPct = Math.min(100, (points / highestThreshold) * 100);

  return (
    <div className="app-shell">
      {/* Header */}
      <div style={{ padding:'18px 22px 14px', borderBottom:'1px solid var(--border)' }}>
        <button className="back-btn" onClick={() => navigate('/dashboard')}>← Back</button>
        <h2 style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:24, letterSpacing:2 }}>Rewards</h2>
      </div>

      <div style={{ flex:1, overflowY:'auto', padding:'20px 22px', paddingBottom:30 }}>
        {loading ? (
          <div className="loading">Loading rewards...</div>
        ) : (
          <>
            {/* Points Balance Card */}
            <div style={{
              background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:18, padding:20,
              marginBottom:20, position:'relative', overflow:'hidden'
            }}>
              <div style={{ position:'absolute', top:-20, right:-20, width:120, height:120, background:`radial-gradient(circle, ${(tier && TIER_COLORS[tier]) || '#f0a020'}18, transparent 70%)`, pointerEvents:'none' }} />
              <p style={{ fontSize:11, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Points Balance</p>
              <div style={{ display:'flex', alignItems:'baseline', gap:10, marginBottom:4 }}>
                <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:44, color:'#f0a020', letterSpacing:1 }}>
                  {points.toFixed(0)} <span style={{ fontSize:18, color:'var(--text-secondary)' }}>points</span>
                </p>
              </div>
              {tier ? (
                <div style={{ display:'inline-flex', alignItems:'center', gap:6, background:`${TIER_COLORS[tier]}18`, border:`1px solid ${TIER_COLORS[tier]}50`, borderRadius:20, padding:'4px 12px', marginTop:4 }}>
                  <span>{TIER_ICONS[tier]}</span>
                  <span style={{ fontSize:12, color:TIER_COLORS[tier], fontWeight:500 }}>{tier} Tier</span>
                </div>
              ) : (
                <p style={{ fontSize:12, color:'var(--text-muted)', marginTop:4 }}>Not yet in a tier — {ladder[0]?.threshold || 250} points unlocks {ladder[0]?.name}</p>
              )}
              {next && (
                <p style={{ fontSize:11, color:'var(--text-muted)', marginTop:10 }}>
                  {next.pointsNeeded} points to <span style={{ color:'var(--text-secondary)' }}>{next.name}</span>
                </p>
              )}
            </div>

            {/* Tier Ladder */}
            <p className="section-label" style={{ marginBottom:12 }}>Your Journey</p>
            <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:16, padding:'20px 16px', marginBottom:24 }}>
              <div style={{ position:'relative', height:4, background:'var(--border)', borderRadius:2, marginBottom:18 }}>
                <div style={{ position:'absolute', left:0, top:0, bottom:0, width:`${progressPct}%`, background:'linear-gradient(90deg, #8a94a6, #f0a020)', borderRadius:2, transition:'width 0.4s' }} />
              </div>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                {ladder.map(t => {
                  const reached = points >= t.threshold;
                  return (
                    <div key={t.name} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, flex:1 }}>
                      <div style={{
                        width:34, height:34, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center',
                        fontSize:15, background: reached ? TIER_COLORS[t.name] : 'var(--bg-base)',
                        border:`2px solid ${reached ? TIER_COLORS[t.name] : 'var(--border)'}`,
                        color: reached ? '#fff' : 'var(--text-muted)'
                      }}>
                        {TIER_ICONS[t.name]}
                      </div>
                      <p style={{ fontSize:10, color: reached ? 'var(--text-primary)' : 'var(--text-muted)', textAlign:'center' }}>{t.name}</p>
                      <p style={{ fontSize:9, color:'var(--text-muted)' }}>{t.threshold}pt</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Referral — hidden entirely while the program's off, rather
                than advertising an offer that won't actually pay out. */}
            {(!referral || referral.enabled) && (
              <>
                <p className="section-label" style={{ marginBottom:12 }}>Invite Friends</p>
                <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:16, padding:18, marginBottom:24 }}>
                  <p style={{ fontSize:13, color:'var(--text-primary)', marginBottom:4 }}>
                    {referral ? `Earn ${referral.effectiveReferrerPoints} points + ${referral.effectiveReferrerCoins} coins per friend` : 'Earn points + coins per friend'}
                  </p>
                  {referral?.promoActive && (
                    <p style={{ fontSize:11, color:'#f0a020', marginBottom:4 }}>⚡ Boosted for a limited time!</p>
                  )}
                  <p style={{ fontSize:12, color:'var(--text-secondary)', marginBottom:14, lineHeight:1.5 }}>
                    Share your link — when a friend you refer gets their first order delivered, you both get rewarded.
                  </p>
                  <div style={{ display:'flex', gap:8, marginBottom:10 }}>
                    <input readOnly value={referralLink} onClick={e => e.target.select()}
                      style={{ flex:1, padding:'10px 12px', background:'var(--bg-base)', border:'1px solid var(--border)', borderRadius:10, color:'var(--text-secondary)', fontSize:12, fontFamily:'DM Sans,sans-serif' }} />
                    <button onClick={handleCopy} style={{ padding:'0 14px', background:'var(--bg-base)', border:'1px solid var(--border)', borderRadius:10, color:'var(--accent-blue)', fontSize:12, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                      {copied ? '✓' : 'Copy'}
                    </button>
                  </div>
                  <button className="btn-primary" onClick={handleShare} style={{ margin:0 }}>Share Invite Link</button>
                </div>
              </>
            )}

            {/* Redeemable Catalog */}
            <p className="section-label" style={{ marginBottom:12 }}>Redeem Points</p>
            {msg && (
              <p style={{ fontSize:12, marginBottom:12, color: msg.startsWith('✅') ? 'var(--accent-green)' : 'var(--accent-red)' }}>{msg}</p>
            )}
            {redeemable.length === 0 ? (
              <p style={{ fontSize:13, color:'var(--text-secondary)' }}>Nothing points-redeemable on the menu right now — check back soon.</p>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {redeemable.map(item => (
                  <div key={item.strain} style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden' }}>
                    <div style={{ display:'flex', gap:12, alignItems:'center', padding:'12px 14px 10px' }}>
                      {/* Same ~2.5:1 banner art as the Order menu, so the box matches that
                          ratio (not square) — see Order.js's list thumbnail for why. */}
                      <div style={{ width:84, height:34, borderRadius:10, flexShrink:0, background:'var(--bg-base)', border:'1px solid var(--border)', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        {item.imageUrl
                          ? <img src={item.imageUrl} alt={item.strain} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                          : <span style={{ fontSize:20 }}>🎁</span>}
                      </div>
                      <div>
                        <p style={{ fontSize:14, fontWeight:600, color:'var(--text-primary)' }}>{item.strain}</p>
                        <p style={{ fontSize:11, color:'var(--text-secondary)' }}>{item.productType || 'Flower'} • {item.category}</p>
                      </div>
                    </div>
                    <div style={{ padding:'0 14px 12px', display:'flex', flexDirection:'column', gap:6 }}>
                      {item.prices.map(p => {
                        const key = `${item.strain}-${p.size}`;
                        const canAfford = points >= p.points;
                        return (
                          <div key={p.size} style={{
                            display:'flex', alignItems:'center', justifyContent:'space-between',
                            padding:'8px 12px', borderRadius:10,
                            background: canAfford ? 'var(--bg-base)' : '#03050f',
                            border:'1px solid var(--border)', opacity: canAfford ? 1 : 0.5
                          }}>
                            <div>
                              <p style={{ fontSize:13, color:'var(--text-primary)' }}>{p.size}</p>
                              <p style={{ fontSize:11, color:'#f0a020' }}>{p.points} points</p>
                            </div>
                            <button onClick={() => handleRedeem(item.strain, p.size, p.points)}
                              disabled={!canAfford || redeeming === key}
                              style={{
                                padding:'7px 16px', borderRadius:9, fontSize:12, cursor: canAfford ? 'pointer' : 'not-allowed',
                                background: canAfford ? 'var(--accent-blue)' : 'var(--bg-card)',
                                color: canAfford ? '#fff' : 'var(--text-muted)', border:'none', fontFamily:'DM Sans,sans-serif'
                              }}>
                              {redeeming === key ? 'Redeeming...' : canAfford ? 'Redeem' : 'Not enough'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Nav Bar */}
      <div className="nav-bar">
        <button className="nav-item" onClick={() => navigate('/dashboard')}><span style={{ fontSize:17 }}>🏠</span>Home</button>
        <button className="nav-item" onClick={() => navigate('/order')}><span style={{ fontSize:17 }}>🛒</span>Order</button>
        <button className="nav-item" onClick={() => navigate('/wallet')}><span style={{ fontSize:17 }}>💰</span>Wallet</button>
        <button className="nav-item active"><span style={{ fontSize:17 }}>🏆</span>Rewards</button>
        <button className="nav-item" onClick={() => navigate('/track')}><span style={{ fontSize:17 }}>🕐</span>Track</button>
      </div>
    </div>
  );
}
